

import React from 'react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { useQuickscan } from '@/contexts/QuickscanContext';
import { ArrowRight, Euro, Leaf, Shield, Zap } from 'lucide-react';
import {
  QUICKSCAN_PRIORITIES,
  QUICKSCAN_PRIORITY_SCALE,
  quickscanPriorityPercentage,
} from '@/lib/quickscan-priorities';

// Alleen de vormgeving hoort hier: de sleutels en labels komen uit de gedeelde
// bron, want de PDF en het adminportaal moeten dezelfde namen tonen.
const presentation: Record<string, { icon: typeof Euro; color: string }> = {
  'cost-savings': { icon: Euro, color: 'text-energy-green' },
  'co2-reduction': { icon: Leaf, color: 'text-energy-green' },
  'reliability': { icon: Shield, color: 'text-energy-blue' },
  'independence': { icon: Zap, color: 'text-energy-orange' },
};

const priorities = QUICKSCAN_PRIORITIES.map((priority) => ({
  ...priority,
  icon: presentation[priority.id]?.icon ?? Zap,
  color: presentation[priority.id]?.color ?? 'text-energy-green',
}));

export const PrioritiesScreen = () => {
  const { data, updateData, nextStep, prevStep } = useQuickscan();

  const handlePriorityChange = (priorityId: string, value: number[]) => {
    updateData('priorities', {
      ...data.priorities,
      [priorityId]: value[0]
    });
  };

  const getPriorityValue = (priorityId: string) => {
    return data.priorities[priorityId] ?? QUICKSCAN_PRIORITY_SCALE.default;
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-energy-green mb-2">
          Wat is het belangrijkst voor jouw bedrijf?
        </h2>
        <p className="text-gray-600">
          Geef aan hoe belangrijk elk aspect voor je is ({QUICKSCAN_PRIORITY_SCALE.min} = niet belangrijk, {QUICKSCAN_PRIORITY_SCALE.max} = zeer belangrijk)
        </p>
      </div>

      <div className="space-y-6 mb-8">
        {priorities.map((priority) => {
          const Icon = priority.icon;
          const value = getPriorityValue(priority.id);
          
          return (
            <div key={priority.id} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center mb-4">
                <Icon className={`w-6 h-6 mr-3 ${priority.color}`} />
                <h3 className="text-lg font-semibold flex-1">{priority.label}</h3>
                <div className="text-2xl font-bold text-energy-green">
                  {value}
                </div>
              </div>
              
              <Slider
                value={[value]}
                onValueChange={(newValue) => handlePriorityChange(priority.id, newValue)}
                max={QUICKSCAN_PRIORITY_SCALE.max}
                min={QUICKSCAN_PRIORITY_SCALE.min}
                step={1}
                className="w-full"
              />
              
              <div className="flex justify-between text-xs text-gray-500 mt-2">
                <span>Niet belangrijk</span>
                <span>Zeer belangrijk</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-8">
        <h3 className="font-semibold mb-3">Jouw prioriteiten samenvatting:</h3>
        <div className="space-y-2">
          {priorities
            .map(priority => ({ ...priority, value: getPriorityValue(priority.id) }))
            .sort((a, b) => b.value - a.value)
            .map((priority, index) => (
              <div key={priority.id} className="flex items-center justify-between">
                <span className="text-sm">
                  {index + 1}. {priority.label}
                </span>
                <div className="flex items-center">
                  <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                    <div 
                      className="bg-energy-green h-2 rounded-full transition-all duration-300"
                      style={{ width: `${quickscanPriorityPercentage(priority.value)}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium w-6">{priority.value}</span>
                </div>
              </div>
            ))}
        </div>
      </div>

      <div className="flex justify-between">
        <Button 
          variant="outline" 
          onClick={prevStep}
          className="border-gray-300"
        >
          Vorige
        </Button>
        
        <Button 
          onClick={nextStep}
          className="bg-[#014336] text-white hover:bg-[#014336]/90"
        >
          Bekijk Resultaten <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};
